import React, { useRef, useState } from "react";

type FrequencyType = "one-off" | "daily" | "weekly" | "monthly";
type MonthlyDateMode = "same-date" | "last-day";

import { format } from "date-fns";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useApp } from "@/contexts/AppContext";
import { ENTITIES as MASTER_ENTITIES } from "@/data/financial-systems";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useTerminology } from "@/hooks/useTerminology";
import { countOccurrences } from "@/lib/recurrence";
import { cn } from "@/lib/utils";
import {
  EndDateError,
  isEndBeforeStart,
  parseDate,
  safeFormatDate,
} from "./dateRangeValidation";
import { type PickerTransaction, TransactionPicker } from "./TransactionPicker";

// Transform entities from master data
const ENTITIES = (MASTER_ENTITIES || []).map((entity) => ({
  id: entity.id,
  name: entity.name + (entity.isHeadOffice ? " (HQ)" : ""),
}));

// Check if multi-entity system
const IS_MULTI_ENTITY = ENTITIES.length > 1;

// Count the monthly occurrence dates inside [start, end] (inclusive), respecting
// the monthly date mode. Mirrors the engine so the preview and engine agree.
function countMonthlyOccurrences(start: Date, end: Date, mode: string): number {
  let count = 0;
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const originalDay = cursor.getDate();
  const endMs = new Date(end);
  endMs.setHours(0, 0, 0, 0);

  while (cursor.getTime() <= endMs.getTime()) {
    count++;
    cursor.setMonth(cursor.getMonth() + 1);
    if (mode === "last-day") {
      cursor.setDate(1);
      cursor.setMonth(cursor.getMonth() + 1);
      cursor.setDate(0);
    } else {
      const daysInMonth = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + 1,
        0,
      ).getDate();
      cursor.setDate(Math.min(originalDay, daysInMonth));
    }
  }
  return count;
}

// Cost categories
const COST_CATEGORIES = [
  { id: "materials", name: "Materials" },
  { id: "equipment", name: "Equipment" },
  { id: "subcontractors", name: "Subcontractors" },
  { id: "utilities", name: "Utilities" },
  { id: "rent", name: "Rent & Facilities" },
  { id: "insurance", name: "Insurance" },
  { id: "marketing", name: "Marketing & Advertising" },
  { id: "software", name: "Software & Subscriptions" },
  { id: "travel", name: "Travel & Entertainment" },
  { id: "professional", name: "Professional Services" },
  { id: "other", name: "Other Operating Costs" },
];

// Demo-mode cost transactions (supplier bills, expenses, cashbook).
// Counterparty lives in `customer` to match PickerTransaction.
const COST_TRANSACTIONS: PickerTransaction[] = [
  {
    id: "bill-001",
    type: "Supplier Bill",
    customer: "ABC Materials Ltd",
    description: "Raw materials order",
    amount: 12500,
    dueDate: "2026-02-15",
  },
  {
    id: "bill-002",
    type: "Supplier Bill",
    customer: "Equipment Rentals Inc",
    description: "Monthly equipment lease",
    amount: 8900,
    dueDate: "2026-02-20",
  },
  {
    id: "exp-001",
    type: "Expense",
    customer: "Tech Solutions Co",
    description: "IT infrastructure upgrade",
    amount: 15000,
    dueDate: "2026-03-01",
  },
  {
    id: "cb-001",
    type: "Cashbook",
    customer: "Office Supplies Direct",
    description: "Stationery and supplies",
    amount: 3200,
    dueDate: "2026-03-05",
  },
  {
    id: "bill-003",
    type: "Supplier Bill",
    customer: "Utilities Provider",
    description: "Quarterly utility bill",
    amount: 5600,
    dueDate: "2026-03-10",
  },
];

// Transaction types for scenario transactions
const TRANSACTION_TYPES = [
  { id: "new-supplier", name: "New Supplier" },
  { id: "one-off-purchase", name: "One-Off Purchase" },
  { id: "capital-expenditure", name: "Capital Expenditure" },
  { id: "loan-repayment", name: "Loan Repayment" },
  { id: "tax-payment", name: "Tax Payment" },
  { id: "insurance-premium", name: "Insurance Premium" },
  { id: "other-cost", name: "Other Cost" },
];

// Recurrence options
const RECURRENCE_OPTIONS = [
  { id: "one-off", name: "One-off" },
  { id: "weekly", name: "Weekly" },
  { id: "monthly", name: "Monthly" },
  { id: "quarterly", name: "Quarterly" },
  { id: "annually", name: "Annually" },
];

// Confidence levels
const CONFIDENCE_LEVELS = [
  { id: "certain", name: "Certain" },
  { id: "probable", name: "Probable" },
  { id: "possible", name: "Possible" },
  { id: "unlikely", name: "Unlikely" },
];

interface AdjustCostsByPercentFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AdjustCostsByPercentForm({
  onSave,
  onCancel,
  onLivePreview,
  initialData,
}: AdjustCostsByPercentFormProps) {
  const isEditing = !!initialData;
  const { mode } = useApp();
  const { getForecastTerm, getCashMovementTerms } = useTerminology();
  const isLive = mode === "live";
  const { baseForecast } = useBaseForecast();
  const [category, setCategory] = useState(
    initialData?.category
      ? (COST_CATEGORIES.find(
          (c) =>
            c.name === initialData.category || c.id === initialData.category,
        )?.id ?? "")
      : "",
  );
  const [adjustmentPercent, setAdjustmentPercent] = useState<number>(
    initialData?.adjustmentPercent ?? 0,
  );
  const [inputValue, setInputValue] = useState<string>(
    String(initialData?.adjustmentPercent ?? 0),
  );
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const [entity, setEntity] = useState<string>(
    initialData?.entity
      ? (ENTITIES.find(
          (e) => e.name === initialData.entity || e.id === initialData.entity,
        )?.id ?? "all")
      : "all",
  );
  const [effectiveDate, setEffectiveDate] = useState<Date | undefined>(
    initialData?.effectiveDate
      ? new Date(initialData.effectiveDate)
      : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    initialData?.endDate ? new Date(initialData.endDate) : undefined,
  );
  const [frequency, setFrequency] = useState<FrequencyType>(
    initialData?.frequency ?? "monthly",
  );
  const [monthlyDateMode, setMonthlyDateMode] = useState<MonthlyDateMode>(
    initialData?.monthlyDateMode ?? "same-date",
  );
  const [reason, setReason] = useState<string>(initialData?.reason ?? "");
  const [confidence, setConfidence] = useState<
    "certain" | "probable" | "possible" | "unlikely"
  >(initialData?.confidence ?? "probable");

  // Does the start day fall on a date that doesn't exist in all months?
  const startDay = effectiveDate?.getDate() ?? 0;
  const showMonthlyDateMode = frequency === "monthly" && startDay >= 29;

  // Sync helpers
  const handleSliderChange = (value: number[]) => {
    setAdjustmentPercent(value[0]);
    setInputValue(String(value[0]));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      const clamped = Math.min(100, Math.max(-100, parsed));
      setAdjustmentPercent(clamped);
    }
  };

  const handleInputBlur = () => {
    const parsed = parseFloat(inputValue);
    if (Number.isNaN(parsed)) {
      setInputValue(String(adjustmentPercent));
    } else {
      const clamped = Math.min(100, Math.max(-100, parsed));
      setAdjustmentPercent(clamped);
      setInputValue(String(clamped));
    }
  };

  // Floating label position: map adjustmentPercent (-100..100) to 0..100%
  const thumbPercent = ((adjustmentPercent + 100) / 200) * 100;

  // In live mode, derive the base cost from the 90-day forecast total outflows.
  // In demo mode, keep the original hardcoded value.
  const forecastDays =
    isLive && baseForecast.length >= 2
      ? Math.max(
          1,
          Math.round(
            (baseForecast[baseForecast.length - 1].date.getTime() -
              baseForecast[0].date.getTime()) /
              86400000,
          ),
        )
      : 90;
  const forecastMonths = Math.max(1, Math.round(forecastDays / 30));
  const liveForecastOutflows = baseForecast.reduce(
    (sum, dp) => sum + dp.outgoing,
    0,
  );
  const estimatedCurrentCost = isLive ? liveForecastOutflows : 540000;
  const adjustmentAmount = (estimatedCurrentCost * adjustmentPercent) / 100;
  const monthlyImpact = isLive
    ? adjustmentAmount / forecastMonths
    : adjustmentAmount / 12;
  const costLabel = isLive
    ? `${getForecastTerm(true)} Period ${getCashMovementTerms().out} (${forecastDays} days)`
    : `Current Annual Cost (${COST_CATEGORIES.find((c) => c.id === category)?.name || "All"})`;

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview) {
      const selectedCategory = COST_CATEGORIES.find((c) => c.id === category);
      const selectedEntity =
        entity === "all" ? null : ENTITIES.find((e) => e.id === entity);
      onLivePreview({
        type: "adjust-costs-percent",
        category: selectedCategory?.name || "",
        adjustmentPercent,
        entity:
          entity === "all"
            ? "All Entities"
            : selectedEntity?.name || "All Entities",
        effectiveDate,
        endDate,
        frequency,
        monthlyDateMode,
        reason,
        confidence,
        adjustmentAmount,
        monthlyImpact,
        estimatedCurrentCost,
      });
    }
  }, [
    category,
    adjustmentPercent,
    entity,
    effectiveDate,
    endDate,
    frequency,
    monthlyDateMode,
    reason,
    confidence,
    adjustmentAmount,
    monthlyImpact,
    estimatedCurrentCost,
    onLivePreview,
  ]);

  const handleSave = () => {
    const selectedCategory = COST_CATEGORIES.find((c) => c.id === category);
    const selectedEntity =
      entity === "all" ? null : ENTITIES.find((e) => e.id === entity);
    onSave({
      type: "adjust-costs-percent",
      category: selectedCategory?.name || "",
      adjustmentPercent,
      entity:
        entity === "all"
          ? "All Entities"
          : selectedEntity?.name || "All Entities",
      effectiveDate,
      endDate,
      frequency,
      monthlyDateMode,
      reason,
      confidence,
      adjustmentAmount,
      monthlyImpact,
      estimatedCurrentCost,
    });
  };

  const isOneOff = frequency === "one-off";
  const endBeforeStart = isEndBeforeStart(effectiveDate, endDate);
  // One-off only needs a category + start date; recurring needs a valid window.
  const isValid = isOneOff
    ? !!category && !!effectiveDate
    : category && effectiveDate && endDate && !endBeforeStart;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {isEditing ? "Edit Cost Adjustment" : "Adjust Costs by %"}
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Category Selection */}
        <div className="space-y-2">
          <Label>Cost Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Select category..." />
            </SelectTrigger>
            <SelectContent>
              {COST_CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Adjustment Percentage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <Label>Cost Adjustment</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Type a value or use the slider
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                min={-100}
                max={100}
                step={1}
                className="w-20 h-7 text-sm text-right px-2"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          {/* Slider with floating bubble */}
          <div className="relative pt-6 pb-1" ref={sliderTrackRef}>
            <div
              className="absolute top-0 -translate-x-1/2 pointer-events-none"
              style={{ left: `${thumbPercent}%` }}
            >
              <span
                className={cn(
                  "text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap",
                  adjustmentPercent > 0
                    ? "bg-red-100 text-red-700"
                    : adjustmentPercent < 0
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600",
                )}
              >
                {adjustmentPercent > 0 ? "+" : ""}
                {adjustmentPercent}%
              </span>
            </div>
            <Slider
              value={[adjustmentPercent]}
              onValueChange={handleSliderChange}
              min={-100}
              max={100}
              step={1}
              className="w-full"
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>-100% (reduction)</span>
            <span>0%</span>
            <span>+100% (increase)</span>
          </div>
        </div>

        {/* Entity Filter - Conditional */}
        {IS_MULTI_ENTITY && (
          <div className="space-y-2">
            <Label>Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITIES.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Date Range — Start + End side by side (one-off has no end date) */}
        <div
          className={cn("grid gap-3", isOneOff ? "grid-cols-1" : "grid-cols-2")}
        >
          <div className="space-y-2">
            <Label>
              Start Date <span className="text-destructive">*</span>
            </Label>
            <DatePicker
              value={effectiveDate}
              onChange={setEffectiveDate}
              dateFormat="d MMM yyyy"
              placeholder="Pick a date"
            />
          </div>

          {!isOneOff && (
            <div className="space-y-2">
              <Label>
                End Date <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                dateFormat="d MMM yyyy"
                placeholder="Pick a date"
                disabledDate={(date) =>
                  effectiveDate ? date <= effectiveDate : false
                }
                aria-invalid={endBeforeStart}
              />
              <EndDateError show={endBeforeStart} />
            </div>
          )}
        </div>

        {/* Frequency */}
        <div className="space-y-2">
          <Label>Frequency</Label>
          <p className="text-xs text-muted-foreground">
            How often does this adjustment apply cash to the forecast?
          </p>
          <div className="grid grid-cols-4 gap-2">
            {(
              [
                { id: "one-off", label: "One-off" },
                { id: "daily", label: "Daily" },
                { id: "weekly", label: "Weekly" },
                { id: "monthly", label: "Monthly" },
              ] as { id: FrequencyType; label: string }[]
            ).map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFrequency(f.id)}
                className={cn(
                  "px-3 py-2 rounded-md border text-sm font-medium transition-colors",
                  frequency === f.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white text-foreground border-border hover:bg-muted",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {frequency === "one-off" &&
              "The full adjustment amount is applied once on the start date with no smoothing."}
            {frequency === "daily" &&
              "The full adjustment amount is spread evenly across every day in the period."}
            {frequency === "weekly" &&
              "The full adjustment amount is split evenly across the weekly occurrences in the period."}
            {frequency === "monthly" &&
              `The full adjustment amount is split evenly across the monthly occurrences in the period${effectiveDate ? ` (from the ${effectiveDate.getDate()}th)` : ""}.`}
          </p>

          {/* Monthly edge case: start date is 29/30/31 */}
          {showMonthlyDateMode && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
              <p className="text-xs font-medium text-amber-800">
                ⚠️ Day {startDay} doesn't exist in all months. How should monthly
                occurrences be handled?
              </p>
              <div className="space-y-1">
                {(
                  [
                    {
                      value: "same-date",
                      label: "Same date each month",
                      desc: "Uses last day of month when the date doesn't exist (e.g. Feb)",
                    },
                    {
                      value: "last-day",
                      label: "Last day of each month",
                      desc: "Always applies on the final day of the month",
                    },
                  ] as { value: MonthlyDateMode; label: string; desc: string }[]
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-start gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="monthlyDateModeCost"
                      value={opt.value}
                      checked={monthlyDateMode === opt.value}
                      onChange={() => setMonthlyDateMode(opt.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="text-xs font-medium text-amber-900">
                        {opt.label}
                      </span>
                      <p className="text-xs text-amber-700">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Confidence Level */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select
            value={confidence}
            onValueChange={(v) => setConfidence(v as typeof confidence)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certain">
                Certain (100% - will definitely happen)
              </SelectItem>
              <SelectItem value="probable">
                Probable (75% - likely to happen)
              </SelectItem>
              <SelectItem value="possible">
                Possible (50% - might happen)
              </SelectItem>
              <SelectItem value="unlikely">
                Unlikely (25% - low probability)
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How confident are you this adjustment will occur?
          </p>
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Cost reduction initiative, supplier negotiation..."
          />
        </div>

        {/* Live Impact Calculations */}
        {(() => {
          // For costs: positive % = bad (red), negative % = good (green) — opposite of revenue
          const isCostIncrease = adjustmentPercent > 0;
          const sign = adjustmentPercent >= 0 ? "+" : "";
          const colourVal = isCostIncrease ? "text-red-700" : "text-green-700";

          // The FULL adjustment amount is applied exactly once across the window,
          // smoothed only by frequency — so the total always equals totalToApply.
          const totalToApply = adjustmentAmount;

          // Occurrence counts are based on the ADJUSTMENT window (start→end),
          // matching the backend engine's distribution.
          const windowDays =
            effectiveDate && endDate
              ? Math.max(
                  1,
                  Math.round(
                    (endDate.getTime() - effectiveDate.getTime()) / 86400000,
                  ),
                )
              : null;
          const dayCount = windowDays !== null ? windowDays + 1 : null;
          const weekCount =
            windowDays !== null ? Math.floor(windowDays / 7) + 1 : null;
          const monthCount =
            effectiveDate && endDate
              ? countMonthlyOccurrences(effectiveDate, endDate, monthlyDateMode)
              : null;

          const perDay = dayCount ? totalToApply / dayCount : 0;
          const perWeek = weekCount ? totalToApply / weekCount : 0;
          const perMonth = monthCount ? totalToApply / monthCount : 0;

          const hasWindow =
            frequency === "one-off" ||
            (frequency === "daily" && dayCount !== null) ||
            (frequency === "weekly" && weekCount !== null) ||
            (frequency === "monthly" && monthCount !== null);
          const totalImpact = hasWindow ? totalToApply : null;

          const frequencyLabel = frequency === "daily" ? "day" : "occurrence";
          const frequencyAmount =
            frequency === "one-off"
              ? totalToApply
              : frequency === "daily"
                ? perDay
                : frequency === "weekly"
                  ? perWeek
                  : perMonth;
          const occurrenceCount =
            frequency === "one-off"
              ? 1
              : frequency === "daily"
                ? dayCount
                : frequency === "weekly"
                  ? weekCount
                  : monthCount;
          const occurrenceUnit =
            frequency === "daily"
              ? "days"
              : frequency === "weekly"
                ? "weekly occurrences"
                : frequency === "monthly"
                  ? "monthly occurrences"
                  : "occurrence";

          const periodLabel = isLive
            ? `forecast period outflows (${forecastDays} days)`
            : "annual cost";
          const totalStr = `£${Math.abs(totalToApply).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const baseStr = `£${Math.abs(estimatedCurrentCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${periodLabel} × ${Math.abs(adjustmentPercent)}%`;
          const explanations: Record<string, string> = {
            "one-off": `${baseStr} = ${sign}${totalStr}, applied once on the start date with no smoothing.`,
            daily: `${baseStr} = ${sign}${totalStr} total, spread evenly across ${dayCount ?? 0} days = ${sign}£${Math.abs(perDay).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per day.`,
            weekly: `${baseStr} = ${sign}${totalStr} total, split evenly across ${weekCount ?? 0} weekly occurrences = ${sign}£${Math.abs(perWeek).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per occurrence.`,
            monthly: `${baseStr} = ${sign}${totalStr} total, split evenly across ${monthCount ?? 0} monthly occurrences = ${sign}£${Math.abs(perMonth).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per occurrence${monthlyDateMode === "last-day" ? " (last day of each month)" : ""}.`,
          };

          return (
            <div
              className={cn(
                "p-4 border rounded-lg space-y-3",
                isCostIncrease
                  ? "bg-red-50 border-red-200"
                  : "bg-green-50 border-green-200",
              )}
            >
              <h4
                className={cn(
                  "font-semibold text-sm",
                  isCostIncrease ? "text-red-900" : "text-green-900",
                )}
              >
                {isCostIncrease ? "Cost Impact" : "Savings Impact"}
              </h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{costLabel}:</span>
                  <span className="font-medium">
                    £
                    {estimatedCurrentCost.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Adjustment:</span>
                  <span className={cn("font-medium", colourVal)}>
                    {sign}
                    {adjustmentPercent}%
                  </span>
                </div>

                {/* Per-frequency amount */}
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">
                    {frequency === "one-off"
                      ? "Applied once:"
                      : `Per ${frequencyLabel}:`}
                  </span>
                  <span className={cn("font-semibold", colourVal)}>
                    {sign}£
                    {Math.abs(frequencyAmount).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Occurrence count + total */}
                {occurrenceCount !== null && totalImpact !== null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {frequency === "one-off"
                        ? "On start date:"
                        : `Over ${occurrenceCount} ${occurrenceUnit}:`}
                    </span>
                    <span className={cn("font-semibold", colourVal)}>
                      {sign}£
                      {Math.abs(totalImpact).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      total
                    </span>
                  </div>
                )}

                {/* Date range summary */}
                {effectiveDate && (frequency === "one-off" || endDate) && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {frequency === "one-off" ? "Date:" : "Period:"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {frequency === "one-off" || !endDate
                        ? format(effectiveDate, "d MMM yyyy")
                        : `${format(effectiveDate, "d MMM yyyy")} → ${format(endDate, "d MMM yyyy")}`}
                    </span>
                  </div>
                )}

                {/* How it's calculated */}
                <div
                  className={cn(
                    "mt-2 pt-2 border-t text-xs leading-relaxed",
                    isCostIncrease ? "text-red-800" : "text-green-800",
                  )}
                >
                  <span className="font-medium">How this is calculated: </span>
                  {explanations[frequency]}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? "Update Adjustment" : "Add Adjustment"}
        </Button>
      </div>
    </div>
  );
}

interface AdjustSpecificTransactionFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
  /** Scenario being edited — scopes the picker's server-side exclusion. */
  scenarioId: number;
  /** Adjustment being edited, so the picker keeps showing its own transaction. */
  excludeAdjustmentId?: number;
  /** Demo mode: transaction ids already adjusted in this scenario — hidden from the picker. */
  usedTransactionIds?: string[];
}

export function AdjustSpecificTransactionForm({
  onSave,
  onCancel,
  onLivePreview,
  initialData,
  scenarioId,
  excludeAdjustmentId,
  usedTransactionIds,
}: AdjustSpecificTransactionFormProps) {
  const isEditing = !!initialData;
  const { mode } = useApp();
  const [transactionId, setTransactionId] = useState(
    initialData?.transactionId ?? "",
  );
  const [newDueDate, setNewDueDate] = useState<Date | undefined>(
    initialData?.newDueDate ? new Date(initialData.newDueDate) : undefined,
  );
  const [adjustedValue, setAdjustedValue] = useState<number | undefined>(
    initialData?.adjustedValue !== undefined &&
      initialData?.adjustedValue !== null
      ? Number(initialData.adjustedValue)
      : undefined,
  );
  const [reason, setReason] = useState(initialData?.reason ?? "");
  const [confidence, setConfidence] = useState<
    "certain" | "probable" | "possible" | "unlikely"
  >(initialData?.confidence ?? "probable");
  // Reconstruct the selected transaction from saved data so edit mode populates
  // before the picker loads its list. The saved counterparty lives under `supplier`.
  const initialSelected: PickerTransaction | null = initialData?.transactionId
    ? {
        id: String(initialData.transactionId),
        type: initialData.transactionType ?? "Supplier Bill",
        customer: initialData.supplier ?? initialData.customer ?? "Unknown",
        description: "",
        amount: initialData.originalAmount ?? initialData.amount ?? 0,
        dueDate: initialData.originalDueDate ?? "",
      }
    : null;
  const [selectedTransaction, setSelectedTransaction] =
    useState<PickerTransaction | null>(initialSelected);

  const originalDueDate = selectedTransaction
    ? parseDate(selectedTransaction.dueDate)
    : null;

  // Calculate date change in days
  const dateChangeDays =
    originalDueDate && newDueDate
      ? Math.round(
          (newDueDate.getTime() - originalDueDate.getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : 0;

  // Calculate value change
  const valueChange =
    selectedTransaction && adjustedValue !== undefined
      ? adjustedValue - selectedTransaction.amount
      : 0;

  // An adjustment resizes a transaction — it must never flip its sign. The baseline sign comes
  // from the transaction itself, since revenue and cost transactions can each be either sign.
  const isOppositeSign =
    adjustedValue !== undefined &&
    !Number.isNaN(adjustedValue) &&
    !!selectedTransaction &&
    selectedTransaction.amount !== 0 &&
    Math.sign(adjustedValue) === -Math.sign(selectedTransaction.amount);

  // Update live preview whenever values change
  React.useEffect(() => {
    if (
      onLivePreview &&
      selectedTransaction &&
      (newDueDate || adjustedValue !== undefined)
    ) {
      onLivePreview({
        type: "adjust-transaction",
        name: `Adjust ${selectedTransaction.customer}`,
        transactionId: selectedTransaction.id,
        supplier: selectedTransaction.customer,
        originalAmount: selectedTransaction.amount,
        amount:
          adjustedValue !== undefined
            ? adjustedValue
            : selectedTransaction.amount,
        adjustedValue:
          adjustedValue !== undefined
            ? adjustedValue
            : selectedTransaction.amount,
        dateChangeDays,
        valueChange,
        reason,
        confidence,
      });
    } else if (onLivePreview) {
      onLivePreview(null);
    }
  }, [
    transactionId,
    newDueDate,
    adjustedValue,
    dateChangeDays,
    valueChange,
    reason,
    confidence,
    selectedTransaction,
    onLivePreview,
  ]);

  const handleSave = () => {
    if (!selectedTransaction) return;

    onSave({
      type: "adjust-transaction",
      name: `Adjust ${selectedTransaction.customer}`,
      transactionId: selectedTransaction.id,
      transactionType: selectedTransaction.type,
      supplier: selectedTransaction.customer,
      originalAmount: selectedTransaction.amount,
      amount:
        adjustedValue !== undefined
          ? adjustedValue
          : selectedTransaction.amount,
      originalDueDate: selectedTransaction.dueDate,
      newDueDate: newDueDate || originalDueDate || undefined,
      adjustedValue:
        adjustedValue !== undefined
          ? adjustedValue
          : selectedTransaction.amount,
      dateChangeDays,
      valueChange,
      reason,
      confidence,
    });
  };

  const isValid =
    transactionId &&
    (newDueDate || adjustedValue !== undefined) &&
    !isOppositeSign;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {isEditing
            ? "Edit Transaction Adjustment"
            : "Adjust Specific Transaction"}
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Transaction Selection */}
        <div className="space-y-2">
          <Label>Transaction</Label>
          <TransactionPicker
            source="Purchase"
            counterpartyLabel="Supplier"
            mode={mode}
            scenarioId={scenarioId}
            excludeAdjustmentId={excludeAdjustmentId}
            demoTransactions={COST_TRANSACTIONS}
            value={transactionId}
            onChange={setTransactionId}
            onSelect={setSelectedTransaction}
            initialSelected={initialSelected}
            disabled={isEditing}
            excludeIds={usedTransactionIds}
            placeholder="Search by supplier, bill number or amount…"
          />
          {isEditing && (
            <p className="text-xs text-muted-foreground">
              To adjust a different transaction, delete this adjustment and add
              a new one.
            </p>
          )}
        </div>

        {/* Show transaction details when selected */}
        {selectedTransaction && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">{selectedTransaction.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Supplier:</span>
              <span className="font-medium">
                {selectedTransaction.customer}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Amount:</span>
              <span className="font-medium">
                £
                {selectedTransaction.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Due Date:</span>
              <span className="font-medium">
                {safeFormatDate(selectedTransaction.dueDate, "MMM dd, yyyy")}
              </span>
            </div>
          </div>
        )}

        {/* New Due Date (Optional) */}
        <div className="space-y-2">
          <Label>New Due Date (optional)</Label>
          <DatePicker
            value={newDueDate}
            onChange={setNewDueDate}
            dateFormat="PPP"
            placeholder="Keep current date"
          />
        </div>

        {/* Adjusted Value (Optional) */}
        <div className="space-y-2">
          <Label>Adjusted Value (optional)</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm">£</span>
            <Input
              type="number"
              value={adjustedValue !== undefined ? adjustedValue : ""}
              onChange={(e) =>
                setAdjustedValue(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              placeholder={
                selectedTransaction
                  ? `Keep current (£${selectedTransaction.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                  : "Keep current value"
              }
              className="flex-1"
              min={
                selectedTransaction && selectedTransaction.amount > 0
                  ? 0
                  : undefined
              }
              max={
                selectedTransaction && selectedTransaction.amount < 0
                  ? 0
                  : undefined
              }
            />
          </div>
          {isOppositeSign && selectedTransaction && (
            <p className="text-xs text-destructive">
              {selectedTransaction.amount > 0
                ? "The adjusted value must remain positive to match the selected transaction — it cannot flip to a negative value."
                : "The adjusted value must remain negative to match the selected transaction — it cannot flip to a positive value."}
            </p>
          )}
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Negotiated payment terms, supplier discount..."
          />
        </div>

        {/* Confidence */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select
            value={confidence}
            onValueChange={(v) => setConfidence(v as typeof confidence)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select confidence..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certain">Certain</SelectItem>
              <SelectItem value="probable">Probable</SelectItem>
              <SelectItem value="possible">Possible</SelectItem>
              <SelectItem value="unlikely">Unlikely</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How confident are you this adjustment will occur?
          </p>
        </div>

        {/* Live Impact Calculations */}
        {selectedTransaction && (newDueDate || adjustedValue !== undefined) && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm text-blue-900">
              Transaction Impact
            </h4>
            <div className="space-y-1 text-sm">
              {newDueDate && dateChangeDays !== 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date Change:</span>
                    <span
                      className={cn(
                        "font-medium",
                        dateChangeDays > 0
                          ? "text-blue-700"
                          : "text-orange-700",
                      )}
                    >
                      {dateChangeDays > 0 ? "+" : ""}
                      {dateChangeDays} days (
                      {dateChangeDays > 0 ? "delayed" : "earlier"})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Date:</span>
                    <span className="font-medium">
                      {format(newDueDate, "MMM dd, yyyy")}
                    </span>
                  </div>
                </>
              )}
              {adjustedValue !== undefined && valueChange !== 0 && (
                <>
                  <div
                    className={cn(
                      "flex justify-between pt-2",
                      newDueDate && dateChangeDays !== 0
                        ? "border-t border-blue-200"
                        : "",
                    )}
                  >
                    <span className="text-muted-foreground">Value Change:</span>
                    <span
                      className={cn(
                        "font-medium",
                        valueChange < 0 ? "text-green-700" : "text-red-700",
                      )}
                    >
                      {valueChange > 0 ? "+" : ""}£
                      {valueChange.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      ({valueChange < 0 ? "reduction" : "increase"})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Value:</span>
                    <span className="font-medium">
                      £
                      {adjustedValue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </>
              )}
              {newDueDate && dateChangeDays > 0 && (
                <div className="pt-2 border-t border-blue-200">
                  <p className="text-xs text-blue-700">
                    💡 Cash preserved: £
                    {selectedTransaction.amount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    from{" "}
                    {safeFormatDate(selectedTransaction.dueDate, "MMM yyyy")} to{" "}
                    {format(newDueDate, "MMM yyyy")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? "Update Adjustment" : "Add Adjustment"}
        </Button>
      </div>
    </div>
  );
}

interface AddScenarioTransactionFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AddScenarioTransactionForm({
  onSave,
  onCancel,
  onLivePreview,
  initialData,
}: AddScenarioTransactionFormProps) {
  const { getScenarioTerm } = useTerminology();
  const isEditing = !!initialData;
  const [transactionType, setTransactionType] = useState(
    initialData?.transactionType
      ? (TRANSACTION_TYPES.find(
          (t) =>
            t.name === initialData.transactionType ||
            t.id === initialData.transactionType,
        )?.id ?? "")
      : "",
  );
  const [expectedDate, setExpectedDate] = useState<Date | undefined>(
    initialData?.expectedDate ? new Date(initialData.expectedDate) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    initialData?.endDate ? new Date(initialData.endDate) : undefined,
  );
  const [amount, setAmount] = useState<number>(initialData?.amount ?? 0);
  const [description, setDescription] = useState(
    initialData?.description ?? "",
  );
  const [confidence, setConfidence] = useState<
    "certain" | "probable" | "possible" | "unlikely"
  >(
    initialData?.confidence
      ? ((CONFIDENCE_LEVELS.find(
          (c) =>
            c.name === initialData.confidence ||
            c.id === initialData.confidence,
        )?.id as "certain" | "probable" | "possible" | "unlikely") ??
          "probable")
      : "probable",
  );
  const [recurrence, setRecurrence] = useState<string>(
    initialData?.recurrence ?? "one-off",
  );
  const [monthlyDateMode, setMonthlyDateMode] = useState<
    "same-date" | "last-day"
  >(initialData?.monthlyDateMode ?? "same-date");
  const [businessContext, setBusinessContext] = useState(
    initialData?.businessContext ?? "",
  );

  const isRecurring = recurrence !== "one-off";
  const startDay = expectedDate?.getDate() ?? 0;
  const showMonthlyDateMode = recurrence === "monthly" && startDay >= 29;

  // The full amount lands at every occurrence — no monthly averaging.
  const occurrenceCount =
    isRecurring && expectedDate && endDate
      ? countOccurrences(expectedDate, endDate, recurrence, monthlyDateMode)
      : null;

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview && amount > 0 && expectedDate) {
      onLivePreview({
        type: "add-scenario-transaction",
        transactionType,
        expectedDate,
        endDate,
        amount,
        description,
        confidence,
        recurrence,
        monthlyDateMode,
        businessContext,
      });
    } else if (onLivePreview) {
      onLivePreview(null);
    }
  }, [
    transactionType,
    expectedDate,
    endDate,
    amount,
    description,
    confidence,
    recurrence,
    monthlyDateMode,
    businessContext,
    onLivePreview,
  ]);

  const handleSave = () => {
    const selectedType = TRANSACTION_TYPES.find(
      (t) => t.id === transactionType,
    );
    const selectedConfidence = CONFIDENCE_LEVELS.find(
      (c) => c.id === confidence,
    );

    onSave({
      type: "add-scenario-transaction",
      transactionType: selectedType?.name || "",
      expectedDate,
      endDate: isRecurring ? endDate : undefined,
      amount,
      description,
      confidence: selectedConfidence?.name || "",
      recurrence,
      monthlyDateMode: recurrence === "monthly" ? monthlyDateMode : undefined,
      businessContext,
    });
  };

  const endBeforeStart = isRecurring && isEndBeforeStart(expectedDate, endDate);
  const isValid =
    transactionType &&
    expectedDate &&
    amount > 0 &&
    description &&
    confidence &&
    (!isRecurring || endDate) &&
    !endBeforeStart;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {isEditing
            ? `Edit ${getScenarioTerm(true, false)} Transaction`
            : `Add ${getScenarioTerm(true, false)} Transaction`}
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Transaction Type */}
        <div className="space-y-2">
          <Label>Transaction Type</Label>
          <Select value={transactionType} onValueChange={setTransactionType}>
            <SelectTrigger>
              <SelectValue placeholder="Select type..." />
            </SelectTrigger>
            <SelectContent>
              {TRANSACTION_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Amount */}
        <div className="space-y-2">
          <Label>Amount</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">£</span>
            <Input
              type="number"
              value={amount || ""}
              onChange={(e) => setAmount(Number(e.target.value))}
              placeholder="0"
              className="flex-1"
            />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Anticipated equipment repair, emergency subcontractor..."
          />
        </div>

        {/* Recurrence */}
        <div className="space-y-2">
          <Label>Recurrence</Label>
          <div className="flex gap-2 flex-wrap">
            {RECURRENCE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setRecurrence(opt.id)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
                  recurrence === opt.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white text-muted-foreground border-gray-200 hover:border-gray-300",
                )}
              >
                {opt.name}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {recurrence === "one-off" &&
              "This cost will appear once on the expected date."}
            {recurrence === "weekly" &&
              "This cost will recur every 7 days from the start date."}
            {recurrence === "monthly" &&
              "This cost will recur on the same date each month."}
            {recurrence === "quarterly" &&
              "This cost will recur every 3 months from the start date."}
            {recurrence === "annually" &&
              "This cost will recur once per year on the same date."}
          </p>
        </div>

        {/* Date row — start + end side by side for recurring */}
        <div className={cn("gap-4", isRecurring ? "grid grid-cols-2" : "")}>
          <div className="space-y-2">
            <Label>{isRecurring ? "Start Date" : "Expected Date"}</Label>
            <DatePicker
              value={expectedDate}
              onChange={setExpectedDate}
              dateFormat="PPP"
              placeholder="Pick a date"
            />
          </div>

          {isRecurring && (
            <div className="space-y-2">
              <Label>End Date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                dateFormat="PPP"
                placeholder="Pick a date"
                disabledDate={(d) => (expectedDate ? d <= expectedDate : false)}
                aria-invalid={endBeforeStart}
              />
              <EndDateError show={endBeforeStart} />
            </div>
          )}
        </div>

        {/* Monthly edge case — 29/30/31 */}
        {showMonthlyDateMode && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
            <p className="text-xs font-medium text-amber-800">
              ⚠️ Your start date is the {startDay}
              {startDay === 29 ? "th" : startDay === 30 ? "th" : "st"} — not all
              months have this date.
            </p>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                <input
                  type="radio"
                  name="costMonthlyDateMode"
                  value="same-date"
                  checked={monthlyDateMode === "same-date"}
                  onChange={() => setMonthlyDateMode("same-date")}
                  className="accent-amber-600"
                />
                Same date each month{" "}
                <span className="text-xs text-amber-700">
                  (uses last day of month where date doesn't exist)
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                <input
                  type="radio"
                  name="costMonthlyDateMode"
                  value="last-day"
                  checked={monthlyDateMode === "last-day"}
                  onChange={() => setMonthlyDateMode("last-day")}
                  className="accent-amber-600"
                />
                Last day of each month{" "}
                <span className="text-xs text-amber-700">
                  (always the final day — different cash timing)
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Confidence Level */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select
            value={confidence}
            onValueChange={(v) => setConfidence(v as typeof confidence)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select confidence..." />
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCE_LEVELS.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  {level.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Business Context */}
        <div className="space-y-2">
          <Label>
            Business Context{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            value={businessContext}
            onChange={(e) => setBusinessContext(e.target.value)}
            placeholder="e.g., Anticipated regulatory requirement, planned capital investment..."
          />
        </div>

        {/* Impact Preview */}
        {amount > 0 && expectedDate && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm text-blue-900">
              Cash Out Impact
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Transaction Amount:
                </span>
                <span className="font-medium text-red-700">
                  -£
                  {amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              {isRecurring ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Recurrence:</span>
                    <span className="font-medium">
                      {RECURRENCE_OPTIONS.find((o) => o.id === recurrence)
                        ?.name ?? recurrence}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date Range:</span>
                    <span className="font-medium">
                      {format(expectedDate, "MMM dd, yyyy")}
                      {endDate ? ` → ${format(endDate, "MMM dd, yyyy")}` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-200">
                    <span className="text-muted-foreground">
                      Amount per occurrence:
                    </span>
                    <span className="font-medium text-red-700">
                      -£
                      {amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  {occurrenceCount !== null && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Occurrences:
                        </span>
                        <span className="font-medium">{occurrenceCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Total over period:
                        </span>
                        <span className="font-medium text-red-700">
                          -£
                          {(amount * occurrenceCount).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected Date:</span>
                  <span className="font-medium">
                    {format(expectedDate, "MMM dd, yyyy")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? "Update Adjustment" : "Add Adjustment"}
        </Button>
      </div>
    </div>
  );
}
